/** One-level thread using the same message rendering and actions as the room. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/api/auth'
import { api } from '@/api/client'
import { loadThreadMessages, refreshCachedMessage, useRoomMessages } from '@/api/store'
import type { Message, Room } from '@/api/types'
import { Composer } from '@/components/Composer'
import { MessageRow } from '@/components/MessageList'
import { Avatar, Button, IconButton, Spinner } from '@/components/ui'
import styles from './ThreadPanel.module.css'

export interface ThreadPanelProps {
  room: Room
  rootId: string
  roomName: string
  currentUsername: string | null
  disabled?: boolean
  disabledReason?: string
  onClose: () => void
}

export function ThreadPanel({ room, rootId, roomName, disabled = false, disabledReason, onClose }: ThreadPanelProps): ReactElement {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const highlightId = searchParams.get('message')
  const [jumpPages, setJumpPages] = useState(10)
  const scrolledTarget = useRef<string | null>(null)
  const panelRef = useRef<HTMLElement>(null)
  const { messages, pending, retry, discard } = useRoomMessages(room.id)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [cursor, setCursor] = useState(0)
  const [editing, setEditing] = useState<Message | null>(null)
  const mounted = useRef(true)
  const busy = useRef(false)

  const load = useCallback(async (after: number): Promise<void> => {
    if (busy.current) return
    busy.current = true
    setLoading(true)
    setError(null)
    try {
      const page = await loadThreadMessages(room.id, rootId, after)
      if (!mounted.current) return
      const next = Math.max(after, ...page.messages.map((message) => message.room_sequence))
      if (page.has_more && next <= after) throw new Error('The thread page did not advance. Try again.')
      setCursor(next)
      setHasMore(page.has_more)
    } catch (failure) {
      if (mounted.current) setError(failure instanceof Error ? failure.message : 'The thread could not be loaded.')
    } finally {
      busy.current = false
      if (mounted.current) setLoading(false)
    }
  }, [room.id, rootId])

  useEffect(() => {
    mounted.current = true
    void load(0)
    return () => { mounted.current = false }
  }, [load])

  const { root, replies } = useMemo(() => ({
    root: messages.find((message) => message.id === rootId),
    replies: messages.filter((message) => message.parent_id === rootId).sort((a, b) => a.room_sequence - b.room_sequence),
  }), [messages, rootId])
  const replyCount = Math.max(root?.reply_count ?? 0, replies.filter((message) => message.deleted_at === null).length)
  const highlighted = messages.find((message) => message.id === highlightId && (message.id === rootId || message.parent_id === rootId))
  useEffect(() => { setJumpPages(10); scrolledTarget.current = null }, [highlightId])
  useEffect(() => {
    if (highlightId === null || loading) return
    if (highlighted !== undefined) {
      if (scrolledTarget.current === highlightId) return
      panelRef.current?.querySelector<HTMLElement>(`[id="thread-message-${highlighted.id}"]`)?.scrollIntoView({ block: 'center' })
      scrolledTarget.current = highlightId
    } else if (hasMore && error === null && jumpPages > 0) {
      setJumpPages((remaining) => remaining - 1)
      void load(cursor)
    }
  }, [highlightId, highlighted, loading, hasMore, error, jumpPages, load, cursor])
  const canModerate = user?.is_admin === true || user?.is_owner === true ||
    room.my_membership?.role === 'owner' || room.my_membership?.role === 'moderator'
  const focusComposer = (): void => { panelRef.current?.querySelector<HTMLElement>('[role="textbox"]')?.focus() }

  const renderMessage = (message: Message): ReactElement => (
    <div key={message.id} className={styles.sharedMessage}>
      <div className={styles.rowHeader}>
        <Avatar name={message.sender_override ?? (message.sender.display_name || message.sender.username)} id={message.sender.id} src={message.sender_avatar_url ?? message.sender.avatar_url} size="sm" decorative />
        <span>{message.sender_override ?? (message.sender.display_name || message.sender.username)}</span>
      </div>
      <MessageRow
        idPrefix="thread-" message={message} room={room} compact selfId={user?.id ?? null}
        canModerate={canModerate} showStamp={false} highlighted={message.id === highlightId}
        delivery={pending.find((entry) => entry.client_message_id === message.client_message_id)}
        editing={false} onEdit={() => setEditing(message)} onEditDone={() => setEditing(null)}
        onRetry={retry} onDiscard={discard} onReply={focusComposer}
        thread={{ count: 0, participants: [], lastAt: null }}
      />
    </div>
  )

  return (
    <section className={styles.panel} aria-label="Thread" ref={panelRef}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title}>Thread</h2>
          <p className={styles.meta}>{replyCount === 1 ? '1 reply' : `${replyCount} replies`}</p>
        </div>
        <IconButton label="Close thread" size="sm" onClick={onClose}>×</IconButton>
      </header>
      <div className={styles.scroller}>
        {root !== undefined ? renderMessage(root) : !loading && error === null ? <p className={styles.missing}>That message is no longer available.</p> : null}
        {replies.map(renderMessage)}
        {loading ? <Spinner label="Loading thread" /> : null}
        {error !== null ? <div role="alert"><p>{error}</p><Button onClick={() => void load(cursor)}>Retry loading thread</Button></div> : null}
        {!loading && error === null && hasMore ? <Button onClick={() => void load(cursor)}>Load more replies</Button> : null}
        {!loading && hasMore && highlightId !== null && highlighted === undefined && jumpPages === 0 ? <Button onClick={() => setJumpPages(10)}>Continue finding reply</Button> : null}
      </div>
      <Composer
        key={`${room.id}-thread-${rootId}`} roomId={room.id} roomName={roomName} replyTo={rootId} autoFocus
        disabled={disabled || root === undefined || root.parent_id != null} disabledReason={disabledReason}
        edit={editing === null ? undefined : {
          id: editing.id, text: editing.text,
          onCancel: () => setEditing(null),
          onSave: async (text) => {
            const updated = await api.patch<Message>(`/messages/${encodeURIComponent(editing.id)}`, { text })
            refreshCachedMessage(updated)
            setEditing(null)
          },
        }}
      />
    </section>
  )
}
